import React from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    ColumnDef,
    flexRender,
} from '@tanstack/react-table';
import { NSTable } from 'bricks';
import RenderIf from 'components/RenderIf/RenderIf';
import { WattsType } from 'views/ProFormaTable/constants';
import { COLUMN_KEYS_BY_MODEL_ID, ColumnKeys } from '../OtherIncomeTable/constants';
import { useProFormaSocketContext } from '../../../../socketContext/ProFormaSocketProvider';
import './TotalPotentialIncomeTable.scss';

export interface ITotalPotentialIncomeTableProps {
    operationBeginIndex: number;
    baseCashFlowColumns: ColumnDef<any>[];
}

const TotalPotentialIncomeTable = ({ operationBeginIndex, baseCashFlowColumns }: ITotalPotentialIncomeTableProps) => {
    const {
        incomeSummary, showCents, formatDisplayValue, operationsCashFlow,
        proForma, wattsType,
    } = useProFormaSocketContext();

    const totalPotentialIncomeCashFlowColumns: ColumnDef<any>[] = baseCashFlowColumns.map((column, cashFlowIndex) => ({
        ...column,
        id: `total-potential-income-${column.id}`,
        footer: () => (
            <RenderIf isTrue={operationBeginIndex}>
                <span className="text-dark">
                    {formatDisplayValue(operationsCashFlow?.[cashFlowIndex]?.totalPotentialIncome || 0, showCents, 'currency')}
                </span>
            </RenderIf>
        ),
    }));

    // eslint-disable-next-line no-unused-vars
    const COLUMNS_BY_KEY: { [key in ColumnKeys]: ColumnDef<any> } = {
        [ColumnKeys.START_DATE]: {
            accessorKey: 'startDate',
            id: 'startDate',
        },
        [ColumnKeys.ANNUAL_GROWTH]: {
            accessorKey: 'annualGrowth',
            id: 'annualGrowth',
        },
        [ColumnKeys.PERCENT_OF_TOTAL_RENT]: {
            accessorKey: 'percentOfTotalRent',
            id: 'percentOfTotalRent',
        },
        [ColumnKeys.PRICE_PER_SF]: {
            accessorKey: 'pricePerSf',
            id: 'pricePerSf',
            footer: () => <span>{formatDisplayValue(incomeSummary.totalPotentialIncome.totalPricePerSf || 0, true, 'currency')}</span>,
        },
        [ColumnKeys.PRICE_PER_SF_PER_YEAR]: {
            accessorKey: 'pricePerSfPerYear',
            id: 'pricePerSfPerYear',
            footer: () => <span>{formatDisplayValue(incomeSummary.totalPotentialIncome.totalPricePerSfPerYear || 0, true, 'currency')}</span>,
        },
        [ColumnKeys.PRICE_PER_UNIT]: {
            accessorKey: 'pricePerUnit',
            id: 'pricePerUnit',
            footer: () => <span>{formatDisplayValue(incomeSummary.totalPotentialIncome.totalPricePerUnit || 0, true, 'currency')}</span>,
        },
        [ColumnKeys.VALUE_PER_RACK_PER_YEAR]: {
            accessorKey: 'valuePerRackPerYear',
            id: 'valuePerRackPerYear',
            footer: () => <span>{formatDisplayValue(incomeSummary.totalPotentialIncome.totalValuePerRackPerYear || 0, true, 'currency')}</span>,
        },
        [ColumnKeys.VALUE_PER_KW_PER_YEAR]: wattsType === WattsType.MEGAWATTS ? {
            accessorKey: 'valuePerMwPerYear',
            id: 'valuePerMwPerYear',
            footer: () => <span>{formatDisplayValue(incomeSummary.totalPotentialIncome.totalValuePerMwPerYear || 0, true, 'currency')}</span>,
        } : {
            accessorKey: 'valuePerKwPerYear',
            id: 'valuePerKwPerYear',
            footer: () => <span>{formatDisplayValue(incomeSummary.totalPotentialIncome.totalValuePerKwPerYear || 0, true, 'currency')}</span>,
        },
    };

    const totalPotentialIncomeColumns: ColumnDef<any>[] = [
        {
            accessorKey: 'name',
            id: 'name',
            footer: () => <span className="px-1">Total potential income</span>,
        },
        ...COLUMN_KEYS_BY_MODEL_ID[proForma.proFormaModelId].map(key => COLUMNS_BY_KEY[key]),
        {
            accessorKey: 'amountPerYear',
            id: 'amountPerYear',
            footer: () => (
                <span className="pr-1">
                    {formatDisplayValue(incomeSummary.totalPotentialIncome.totalAmountPerYear || 0, showCents, 'currency')}
                </span>
            ),
        },
        ...totalPotentialIncomeCashFlowColumns,
    ];

    const table = useReactTable({
        data: [],
        columns: totalPotentialIncomeColumns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    return (
        <NSTable className={`NSTable TotalPotentialIncomeTable--${proForma.proFormaModelName.replace(/ /g, '-')} mb-1`}>
            <tfoot className="NSTable__tfoot">
                {table.getFooterGroups().map(footerGroup => (
                    <tr key={footerGroup.id} className="NSTable__tfoot__tr">
                        {footerGroup.headers.map(header => (
                            <th key={header.id} className="NSTable__tfoot__tr__th p-1">
                                {flexRender(
                                    header.column.columnDef.footer,
                                    header.getContext(),
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
            </tfoot>
        </NSTable>
    );
};

export default TotalPotentialIncomeTable;
